import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyMarketKey,
  ShopifyMarketRegionKey,
  ShopifyMarketRegionToMarketKey,
  ShopifyMarketRegionToShopKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyMarketRegionKey,
  defaultFilterableApiIdentifiers: ["code"],
  "2022-04": {
    name: "Shopify Market Region",
    graphQLTypename: "MarketRegion",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "id",
            graphQLFieldKey: "id",
          },
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "name",
            graphQLFieldKey: "name",
          },
        },
      },
      {
        name: "Code",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "code",
            graphQLFieldKey: "code",
            graphQLFragments: ["MarketRegionCountry"],
          },
        },
      },
      {
        name: "Market",
        fieldType: FieldType.BelongsTo,
        key: ShopifyMarketRegionToMarketKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "market",
            skipGraphQLSelection: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyMarketKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyMarketRegionToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "markets",
      resource: "market.regions",
    },
  },
  "2026-07": {
    syncConfig: {
      type: "graphql_sublist",
      parent: "markets",
      resource: "market.conditions.regionsCondition.regions",
    },
    fields: [
      {
        // 2026-07 introduces the MarketRegionSubdivision concrete type. Without a fragment for it
        // those records would mirror with only the interface-level fields (id/name) populated.
        // https://shopify.dev/changelog/markets-apis-now-support-marketregionsubdivision
        //
        // Note the existing "Code" field cannot simply
        // add a MarketRegionSubdivision fragment: MarketRegionCountry.code is `CountryCode!` (enum)
        // while MarketRegionSubdivision.code is `String!`, and selecting both under the same response
        // key is invalid GraphQL (SameResponseShape), so the subdivision code is aliased into its own
        // field instead.
        name: "Subdivision Code",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "subdivisionCode",
            graphQLOnlyField: true,
            graphQLFragments: [{ type: "MarketRegionSubdivision", selection: "subdivisionCode: code" }],
          },
        },
      },
    ],
  },
};

export const ShopifyMarketRegion = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
